import { Injectable, Injector } from '@angular/core';
import { Observable, Subscription, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { BroadcastService } from '../../services/broadcast.service';
import { ThemeBundle } from '../models/theme-bundle.model';
import { ThemeStatus } from '../models/theme-status.model';
import { ThemeApiService } from '../services/theme-api.service';
import { ThemeAllDetails } from './../models/theme.model';

import { Store } from '@ngrx/store';
import { ToroLocationState } from './../../accounts/store/toro-location/toro-location.entity';
import { getCurrentLocationDetail } from './../../accounts/store/toro-location/toro-location.selectors';
import { LightingState } from './../../lighting/store/lighting.entity';
import { getAllThemes } from './../../lighting/store/lighting.selector';
import { ThemeState } from './../../lighting/store/themes/theme.entity';

@Injectable()
export class ThemeManagerService {
    private _themeStatusDict: { [locationId: string]: ThemeStatus[] } = {};
    private _themeBundlesDict: { [locationId: string]: ThemeBundle[] } = {};
    currentLocationSubscription: Subscription;

    constructor(private broadcastService: BroadcastService,
        private store: Store<ToroLocationState>,
        private lightingStore: Store<LightingState>,
        private injector: Injector,
        private themeApiService: ThemeApiService) {

        this.broadcastService.userSignedOut.subscribe(() => {
            this.clearCache();
        });
    }

    private clearCache() {
        this._themeStatusDict = {};
    }

    public getThemeBundle(locationId: number, themeId: number): ThemeBundle {
        let themeBundlesForLocation = this._themeBundlesDict[locationId];
        let themeBundle = null;
        if (themeBundlesForLocation) {
            themeBundle = themeBundlesForLocation.find(theme => theme.Theme.Id === themeId) || null;
        }
        if (themeBundle === null || themeBundle === undefined || themeBundlesForLocation === undefined || themeBundlesForLocation === null) {

            this.currentLocationSubscription = this.lightingStore.select(getAllThemes).subscribe(themes => {
                if (themes && themes !== null && themes.length > 0) {
                    let themeBundles: ThemeBundle[] = [];
                    let themeStatuses = [];
                    let locId = themes[0].LocationId;
                    this.getThemeStatuses(locId).subscribe(statuses => {
                        themeStatuses = statuses;
                    });

                    themes.forEach(theme => {

                        const themeStatus = themeStatuses.find(status => status.ThemeId === theme.Id);
                        themeBundles.push(new ThemeBundle(theme, themeStatus, this.injector));
                    });

                    this.setThemeBundles(locId, themeBundles);

                    themeBundlesForLocation = this._themeBundlesDict[locId];
                }
            });
        }
        else {
            return themeBundle;
        }
        if (themeBundlesForLocation) {
            return themeBundlesForLocation.find(bundle => bundle.Theme.Id === themeId) || null;
        }
        return null;
    }

    public getAllThemeBundles(locationId: number): ThemeBundle[] {
        let themeBundlesForLocation = this._themeBundlesDict[locationId];
        if (themeBundlesForLocation) {
            return themeBundlesForLocation.length > 0 ? themeBundlesForLocation : null;
        }
        else {
            this.currentLocationSubscription = this.store.select(getCurrentLocationDetail).subscribe(loc => {
                if (loc && loc !== null) {

                    let themeBundles: ThemeBundle[] = [];
                    let themesStatuses = [];
                    this.getThemeStatuses(loc.Id).subscribe(statuses => {
                        themesStatuses = statuses;
                    });

                    loc.Themes.forEach(theme => {

                        const themeStatus = themesStatuses.find(status => status.ThemeId === theme.Id);
                        themeBundles.push(new ThemeBundle(theme, themeStatus, this.injector));

                    });

                    this.setThemeBundles(loc.Id, themeBundles);

                    themeBundlesForLocation = this._themeBundlesDict[loc.Id];
                }
            });

        }
        if (themeBundlesForLocation) {
            return themeBundlesForLocation || null;
        }

        return null;
    }

    public setThemeBundles(locationId: number, themeBundles: ThemeBundle[]) {
        this._themeBundlesDict[locationId] = themeBundles;
    }

    public getThemeStatuses(locationId: number, cached: boolean = true): Observable<ThemeStatus[]> {
        if (cached && this._themeStatusDict[locationId]) {
            return of(this._themeStatusDict[locationId]);
        }

        return this.themeApiService.getThemeStatuses(locationId)
            .pipe(
                map((response: ThemeStatus[]) => {
                    this._themeStatusDict[locationId] = [];
                    response.forEach(themeStatus => {
                        this._themeStatusDict[locationId].push(new ThemeStatus(themeStatus));
                    });

                    return this._themeStatusDict[locationId];
                })
            );
    }

    public forceThemeOn(themeId: number, duration: number = 0): Observable<boolean> {
        return this.themeApiService.forceThemeOn(themeId, duration);
    }

    public forceThemeOff(themeId: number): Observable<boolean> {
        return this.themeApiService.forceThemeOff(themeId);
    }

    public forceAllThemesOn(locationId: number): Observable<boolean> {
        return this.themeApiService.forceAllThemesOn(locationId);
    }

    public forceAllThemesOff(locationId: number): Observable<boolean> {
        return this.themeApiService.forceAllThemesOff(locationId);
    }

    public createTheme(locationId: number, createThemeModel): Observable<any> {
        return this.themeApiService.createTheme(locationId, createThemeModel);
    }

    public updateTheme(themeId: number, updateThemeModel): Observable<boolean> {
        return this.themeApiService.updateTheme(themeId, updateThemeModel);
    }

    public getThemes(locationId: number): Observable<any> {
        return this.themeApiService.getThemes(locationId);
    }

    public deleteTheme(themeId: number): Observable<boolean> {
        return this.themeApiService.deleteTheme(themeId);
    }

    public getThemeDetails(themeId: number): Observable<ThemeAllDetails> {
        return this.themeApiService.getThemeDetails(themeId);
    }
    // Helper Methods

    ngOnDestroy() {
        if (this.currentLocationSubscription) {
            this.currentLocationSubscription.unsubscribe();
        }
    }
}
